<template>
  <div class="ba-trend ba-sitemap">
    <ba-sitemap-pane-control
      v-if="currentRoot && !noData"
      :pane-scale="Math.round(paneScale * 100)"
      :thumbnail-hidden="thumbnailHidden"
      :nodes-expanded="allNodesExpanded"
      :sitemap-mode="sitemapMode"
      :subdomain-selected="!!journeyOptions.subSid"
      :report-showing="openNodeReport"
      @onPaneScaleChange="onPaneScaleChange"
      @onPaneScaleFit="onPaneScaleFit"
      @toggleThumbnail="toggleThumbnail"
      @toggleNodesExpanded="toggleNodesExpanded"
      @changeSitemapMode="changeSitemapMode"
    />

    <div
      v-if="currentRoot && !noData"
      class="pane-wrap"
      :class="{ disabled: openNodeReport, 'node-reporting': reportShowing }"
      ref="paneWrap"
    >
      <div
        class="pane"
        ref="pane"
        :style="{
          minWidth: `calc(100% + ${paneExtraPadding[3]}px)`,
          minHeight: `calc(100% + ${paneExtraPadding[0]}px)`,
          padding: `${panePadding[0] + paneExtraPadding[0]}px ${panePadding[1] + paneExtraPadding[1]}px ${panePadding[2] + paneExtraPadding[2]}px ${panePadding[3] + paneExtraPadding[3]}px`,
          transform: `translate3d(${panePosition[0]}px, ${panePosition[1]}px, 0) scale(${paneScale})`
        }"
      >
        <ba-sitemap-node-list
          v-if="currentRoot"
          ref="nodeList"
          :root="currentRoot"
          :keyword="keyword"
          :search-results="searchResults"
          :expanded-map="expandedMap"
          :selected-key="openNodeReport ? selectedNodeKey : null"
          :pane-wrap-size="paneWrapSize"
          :default-thumbnail="defaultThumbnail"
          :thumbnail-hidden="thumbnailHidden"
          :report-showing="openNodeReport"
          @select="showReport"
          @toggle="toggleNode"
          @selectSearchResult="selectSearchResult"
        />
      </div>
    </div>

    <div v-if="!apiLoading && noData" class="sitemap-no-data">
      <h3>{{ $t('journey.noData.title') }}</h3>
      <p>{{ $t('journey.noData.description') }}</p>
    </div>

    <ba-schema-inspection-floating-button
      v-if="currentRoot && !apiLoading && !noData && !reportShowing"
      :disabled="schemaInspectionTargets.length === 0"
      :requested="schemaInspectionRequested"
      @click="showSchemaInspectionGuide"
    />

    <dimmed
      v-if="schemaInspectionGuideShowing"
      :close-to-click="true"
      @close="schemaInspectionGuideShowing = false"
    >
      <ba-schema-inspection-guide-modal
        :domain="schemaInspectionScope.domain"
        :sub-domain="schemaInspectionScope.subDomain"
        :first-path="schemaInspectionScope.firstPath"
        @close="schemaInspectionGuideShowing = false"
        @confirm="confirmSitemapSchemaInspection"
      />
    </dimmed>

    <button
      v-if="!reportShowing"
      type="button"
      class="pane-navigate left"
      @click="movePaneX('left')"
    >
      <i class="icon icon--arrow-left-gray"></i>
    </button>
    <button
      v-if="!reportShowing"
      type="button"
      class="pane-navigate right"
      @click="movePaneX('right')"
    >
      <i class="icon icon--arrow-right-gray"></i>
    </button>

    <transition name="node-report">
      <div class="node-report" v-if="reportShowing && openNodeReport">
        <ba-sitemap-node-report
          :node="selectedNode"
          :capture-processing="captureProcessing"
          @capture="onCapture"
        />
        <button class="close-report" @click="hideReport">
          <i class="icon icon--close-arrow-right"></i>
        </button>
      </div>
    </transition>

    <div
      v-if="openNodeReport"
      class="blocker"
      @click="hideReport"
    />

    <dimmed v-if="capturingPage">
      <ba-capture
        :page-data="capturingPage"
        :capturing-device="capturingDevice"
        :whole-capture-processing="wholeCaptureProcessing"
        @onWholeCaptureProcessingChange="wholeCaptureProcessing = $event"
        @onCaptureProcessing="onCaptureProcessing"
        @onCloseLayer="capturingPage = null"
      />
    </dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { getS3Url } from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import { createSitemapSchemaReport, getSitemapTree } from '@/api/ranking'
import { checkCaptureExist } from '@/api/capture'

import BaSitemapPaneControl from '@/components/sitemap/PaneControl'
import BaSitemapNodeList from '@/components/sitemap/NodeList'
import BaSitemapNodeReport from '@/components/sitemap/NodeReport'
import BaSchemaInspectionFloatingButton from '@/components/sitemap/SchemaInspectionFloatingButton'
import BaSchemaInspectionGuideModal from '@/components/sitemap/SchemaInspectionGuideModal'
import BaCapture from '@/components/journey/Capture'
import Dimmed from '@/components/ui/Dimmed'
import AppDialog from '@/components/ui/AppDialog'

const wheelEventName = 'onwheel' in document ? 'wheel' : 'DOMMouseScroll' in document ? 'DOMMouseScroll' : 'mousewheel'
const DEFAULT_PANE_SCALE = 1
const DEFAULT_PANE_POSITION = [-150, -170]
const SCHEMA_INSPECTION_INITIAL_POLLING_DURATION = 1 * 60 * 1000
const SCHEMA_INSPECTION_FAST_POLLING_DURATION = 10 * 60 * 1000
const SCHEMA_INSPECTION_MAX_POLLING_DURATION = 30 * 60 * 1000
const SCHEMA_INSPECTION_INITIAL_POLLING_INTERVAL = 15 * 1000
const SCHEMA_INSPECTION_FAST_POLLING_INTERVAL = 30 * 1000
const SCHEMA_INSPECTION_SLOW_POLLING_INTERVAL = 2 * 60 * 1000
const openSchemaInspectionDialog = create(AppDialog)

function getEventPoint (e) {
  if (e.touches) {
    e = e.touches[0] || e.changedTouches[0]
  }
  return [e.pageX || e.clientX, e.pageY || e.clientY]
}

function parseImagePath (imagePath) {
  if (!imagePath) return null
  if (typeof imagePath === 'object') return imagePath
  const source = String(imagePath).trim()
  if (!source) return null

  try {
    return JSON.parse(source.replace(/'/g, '"'))
  } catch (e) {
    return source
  }
}

function normalizeDeviceValues (value) {
  const parsedValue = parseImagePath(value)
  if (typeof parsedValue === 'object' && parsedValue) return parsedValue
  return parsedValue ? { desktop: parsedValue } : {}
}

function mergeDeviceValues (currentValue, nextValue) {
  const normalizedCurrentValues = normalizeDeviceValues(currentValue)
  const normalizedNextValues = normalizeDeviceValues(nextValue)

  return Object.entries(normalizedNextValues).reduce((values, [key, value]) => {
    if (value) values[key] = value
    return values
  }, { ...normalizedCurrentValues })
}

function toImageUrl (value) {
  if (!value) return ''
  const imageValue = typeof value === 'string' ? value : (value.desktop || value.tablet || value.phone || '')
  if (!imageValue) return ''
  if (/^https?:\/\//.test(imageValue)) return imageValue
  return getS3Url(imageValue)
}

function normalizeNode (node, indexPath, parentKey = null) {
  const key = indexPath.join('-')
  const parsedImagePath = parseImagePath(node.imagePath)
  const items = (node.items || []).map((child, index) => normalizeNode(child, indexPath.concat(index), key))
  return {
    ...node,
    items,
    _key: key,
    _parentKey: parentKey,
    _thumbnailUrl: toImageUrl(parsedImagePath)
  }
}

function createFirstPathRootNode (node, journeyOptions) {
  const path = node.pathname || node.path || ''
  const domain = node.domain || ''
  const baseDomain = (journeyOptions && journeyOptions.domain) || ''
  const suffix = `.${baseDomain}`
  const subdomainLabel = baseDomain && domain.endsWith(suffix)
    ? domain.slice(0, -suffix.length)
    : domain
  const rootNode = {
    ...node,
    _virtualGroupNode: true
  }

  if (!domain || path.indexOf('/') !== 0) return rootNode

  return {
    ...rootNode,
    _sitemapDisplayName: path,
    _sitemapDisplayDomain: subdomainLabel,
    _sitemapDisplayDomainTitle: domain
  }
}

function createDomainRoot (nodes, journeyOptions, sitemapMode) {
  const rootNodes = sitemapMode === 'firstPath'
    ? nodes.map(node => createFirstPathRootNode(node, journeyOptions))
    : nodes.map(node => ({
      ...node,
      _virtualGroupNode: true
    }))

  if (rootNodes.length <= 1) return rootNodes

  const firstNode = rootNodes[0] || {}
  const selectedDomain = journeyOptions.subSid ? journeyOptions.subDomain : journeyOptions.domain
  const domainSource = selectedDomain || firstNode.domain || firstNode.realDomain || firstNode.origin || firstNode.name || ''
  const protocol = firstNode.origin && firstNode.origin.indexOf('://') > -1 ? firstNode.origin.split('://')[0] : 'https'
  const domain = domainSource.indexOf('://') > -1 ? domainSource.split('://')[1] : domainSource
  const origin = domainSource.indexOf('://') > -1 ? domainSource : `${protocol}://${domain}`

  return [{
    name: journeyOptions.subSid ? domain : `*.${domain.replace(/^\*\./, '')}`,
    path: '/',
    pathname: '/',
    depth: 0,
    origin,
    domain,
    realDomain: domain,
    parentSid: firstNode.parentSid,
    sid: journeyOptions.sid,
    title: domain,
    count: rootNodes.reduce((sum, node) => sum + (Number(node.count) || 0), 0),
    urlCount: rootNodes.reduce((sum, node) => sum + (Number(node.urlCount) || 0), 0),
    _virtualGroupNode: true,
    items: rootNodes
  }]
}

function flattenNodes (nodes) {
  const list = []
  const walk = node => {
    list.push(node)
    node.items.forEach(walk)
  }
  nodes.forEach(walk)
  return list
}

function createDefaultExpandedMap (root) {
  const expandedMap = {}
  if (!root) return expandedMap

  expandedMap[root._key] = true
  root.items.forEach(node => {
    if (node.items.length) {
      expandedMap[node._key] = true
    }
  })
  return expandedMap
}

export default {
  name: 'BaSitemap',
  components: {
    BaSitemapPaneControl,
    BaSitemapNodeList,
    BaSitemapNodeReport,
    BaSchemaInspectionFloatingButton,
    BaSchemaInspectionGuideModal,
    BaCapture,
    Dimmed
  },
  data () {
    return {
      roots: [],
      allNodes: [],
      nodeByKey: {},
      nodeByGUrlId: {},
      activeRootKey: null,
      selectedNodeKey: null,
      reportShowing: false,
      openNodeReport: false,
      expandedMap: {},
      keyword: '',
      paneScale: DEFAULT_PANE_SCALE,
      panePosition: [...DEFAULT_PANE_POSITION],
      thumbnailHidden: true,
      schemaInspectionLoading: false,
      schemaInspectionRequested: false,
      schemaInspectionGuideShowing: false,
      apiLoading: false,
      noData: false,
      capturingPage: null,
      capturingDevice: null,
      captureProcessing: null,
      wholeCaptureProcessing: [],
      paneWrapSize: [0, 0],
      panePadding: [108, 40, 20, 80],
      paneExtraPadding: [130, 190, 140, 150],
      sitemapMode: 'subdomain',
      formerPanePosition: null
    }
  },
  computed: {
    ...mapState([
      'journeyOptions'
    ]),
    dataOption () {
      if (!this.journeyOptions) return null
      const option = {
        sid: this.journeyOptions.sid,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath
      }
      if (this.sitemapMode === 'firstPath' && !this.journeyOptions.subSid) option.mode = 'firstPath'
      return option
    },
    currentRoot () {
      return this.roots.find(root => root._key === this.activeRootKey) || null
    },
    currentRootNodes () {
      if (!this.currentRoot) return []
      const rootKeyPrefix = `${this.currentRoot._key}-`
      return this.allNodes.filter(node => node._key === this.currentRoot._key || node._key.indexOf(rootKeyPrefix) === 0)
    },
    defaultThumbnail () {
      return (this.currentRootNodes.find(node => node._thumbnailUrl) || {})._thumbnailUrl || ''
    },
    selectedNode () {
      return this.nodeByKey[this.selectedNodeKey] || null
    },
    allNodesExpanded () {
      if (!this.currentRoot) return false
      const expandableNodes = this.currentRootNodes.filter(node => node.items && node.items.length)
      return expandableNodes.length > 0 && expandableNodes.every(node => this.expandedMap[node._key])
    },
    schemaInspectionTargets () {
      return this.currentRootNodes
        .filter(node => node.gUrlId != null && node.gUrlId !== '')
        .slice(0, 100)
    },
    schemaInspectionScope () {
      const options = this.journeyOptions || {}
      return {
        domain: options.domain || '',
        subDomain: options.subSid ? options.subDomain : '',
        firstPath: options.firstPath || ''
      }
    },
    searchResults () {
      const keyword = this.keyword.toLowerCase()
      if (!keyword) return []
      return this.allNodes
        .filter(node => {
          return [node.name, node.pathname, node.title, node.domain, node.gUrlId, node.sid]
            .filter(Boolean)
            .some(value => String(value).toLowerCase().includes(keyword))
        })
        .slice(0, 80)
    }
  },
  watch: {
    'journeyOptions.subSid' (value) {
      if (value && this.sitemapMode === 'firstPath') {
        this.sitemapMode = 'subdomain'
      }
    }
  },
  mounted () {
    this.setPaneDraggable()
    this.onResize()
    EventBus.$onSticky('loadPaneData', () => {
      this.loadData()
      EventBus.$clearSticky('loadPaneData')
    })
    EventBus.$onSticky('loadPaneNoData', (value) => {
      this.resetReportState()
      EventBus.$emit('onLoading', false)
      this.roots = []
      this.allNodes = []
      this.nodeByKey = {}
      this.nodeByGUrlId = {}
      this.activeRootKey = null
      this.selectedNodeKey = null
      this.expandedMap = {}
      this.noData = value
      this.apiLoading = false
      this.resetSchemaInspectionState()
      EventBus.$clearSticky('loadPaneNoData')
    })
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy () {
    clearTimeout(this._capturePollTimer)
    this.stopSchemaInspectionPolling()
    cancelAnimationFrame(this._openNodeReportFrame)
    clearTimeout(this._closeNodeReportTimer)
    this.unsetPaneDraggable()
    EventBus.$off('loadPaneData')
    EventBus.$off('loadPaneNoData')
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('mousemove', this.onPointerMove)
    document.removeEventListener('mouseup', this.onPointerUp)
    document.removeEventListener('touchmove', this.onPointerMove)
    document.removeEventListener('touchend', this.onPointerUp)
  },
  methods: {
    loadData () {
      if (!this.dataOption) return

      this.resetSchemaInspectionState()
      const requestId = Date.now()
      this._lastRequestId = requestId
      this.apiLoading = true
      this.noData = false
      EventBus.$emit('onLoading', true)

      getSitemapTree(this.dataOption, true).then(result => {
        if (this._lastRequestId !== requestId) return

        this.applySitemapTreeResult(result, {
          resetReport: true,
          resetExpanded: true,
          resetView: true
        })
        this.restoreSchemaInspectionPolling()
        this.apiLoading = false
        EventBus.$emit('onLoading', false)
        this.$nextTick(() => {
          this.unsetPaneDraggable()
          this.setPaneDraggable()
          this.onResize(true)
          this.setPaneOverflowPosition()
        })
      }).catch(() => {
        if (this._lastRequestId !== requestId) return
        this.roots = []
        this.allNodes = []
        this.nodeByKey = {}
        this.nodeByGUrlId = {}
        this.activeRootKey = null
        this.resetReportState()
        this.expandedMap = {}
        this.noData = true
        this.apiLoading = false
        EventBus.$emit('onLoading', false)
      })
    },
    applySitemapTreeResult (result, { resetReport = false, resetExpanded = false, resetView = false } = {}) {
      const tree = createDomainRoot(Array.isArray(result) ? result : ((result && result.tree) || []), this.journeyOptions, this.sitemapMode)
      const previousRoot = this.currentRoot
      const previousRootIdentity = previousRoot && (previousRoot.origin || previousRoot.domain || previousRoot.name)
      const roots = tree.map((root, index) => normalizeNode(root, [index]))
      const allNodes = flattenNodes(roots)
      const nodeByKey = allNodes.reduce((map, node) => {
        map[node._key] = node
        return map
      }, {})
      const nodeByGUrlId = allNodes.reduce((map, node) => {
        const gUrlId = Number(node.gUrlId)
        if (Number.isFinite(gUrlId)) map[gUrlId] = node
        return map
      }, {})
      const nextRoot = roots.find(root => {
        return previousRootIdentity && [root.origin, root.domain, root.name].includes(previousRootIdentity)
      }) || roots[0] || null

      this.roots = roots
      this.allNodes = allNodes
      this.nodeByKey = nodeByKey
      this.nodeByGUrlId = nodeByGUrlId
      this.activeRootKey = nextRoot ? nextRoot._key : null
      this.noData = !roots.length

      if (resetReport) this.resetReportState()
      if (resetExpanded) this.expandedMap = createDefaultExpandedMap(nextRoot)
      if (resetView) this.resetPaneView()
      if (this.selectedNodeKey && !this.nodeByKey[this.selectedNodeKey]) {
        this.resetReportState()
      }
    },
    getSchemaInspectionPayload () {
      const parentSid = this.getSchemaInspectionParentSid()
      return {
        parentSid,
        gUrlIds: this.schemaInspectionTargets
          .map(node => Number(node.gUrlId))
          .filter(gUrlId => Number.isFinite(gUrlId)),
        lang: this.$i18n.locale || 'ko'
      }
    },
    getSchemaInspectionParentSid () {
      const source = (this.currentRoot && this.currentRoot.parentSid) ||
        (this.schemaInspectionTargets[0] && this.schemaInspectionTargets[0].parentSid) ||
        (this.journeyOptions && (this.journeyOptions.parentSid || this.journeyOptions.sid)) ||
        ''
      return String(source).replace(/^s/, '')
    },
    showSchemaInspectionGuide () {
      if (this.schemaInspectionLoading) return
      this.schemaInspectionGuideShowing = true
    },
    confirmSitemapSchemaInspection () {
      this.schemaInspectionGuideShowing = false
      this.inspectSitemapSchema()
    },
    inspectSitemapSchema () {
      if (this.schemaInspectionLoading) return

      const targets = this.schemaInspectionTargets
      if (!targets.length) return

      const payload = this.getSchemaInspectionPayload()
      if (!payload.parentSid || !payload.gUrlIds.length) return

      this.schemaInspectionLoading = true
      targets.forEach(node => {
        this.$set(node, 'schemaInspectionLoading', true)
      })

      createSitemapSchemaReport(payload, true)
        .then(result => {
          const reportList = (result && result.reportList) || []
          const requestedGUrlIdSet = new Set(reportList.map(report => Number(report.gUrlId)))

          targets.forEach(node => {
            const gUrlId = Number(node.gUrlId)
            const isRequested = requestedGUrlIdSet.has(gUrlId)

            this.$set(node, 'schemaInspectionLoading', false)
            if (isRequested) {
              this.$set(node, 'schemaInspection', {
                loading: true,
                reportStatus: 'ing'
              })
            }
          })

          this.schemaInspectionRequested = true
          this.startSchemaInspectionPolling([...requestedGUrlIdSet])

          openSchemaInspectionDialog({
            customData: {
              type: 'alert',
              iconClass: 'icon--check-green-size50',
              title: this.$t('observedSitemap.inspection.requestCompleted.title'),
              description: this.$t('observedSitemap.inspection.requestCompleted.description')
            },
            ok: this.$t('common.confirm')
          })
        })
        .catch(() => {
          targets.forEach(node => {
            this.$set(node, 'schemaInspectionLoading', false)
            if (node.schemaInspection && node.schemaInspection.loading) {
              this.$delete(node, 'schemaInspection')
            }
          })
        })
        .finally(() => {
          this.schemaInspectionLoading = false
        })
    },
    resetSchemaInspectionState () {
      this.stopSchemaInspectionPolling()
      this.schemaInspectionLoading = false
      this.schemaInspectionRequested = false
      this.schemaInspectionGuideShowing = false
    },
    startSchemaInspectionPolling (gUrlIds) {
      const pendingGUrlIds = gUrlIds
        .map(gUrlId => Number(gUrlId))
        .filter(gUrlId => Number.isFinite(gUrlId))

      this.stopSchemaInspectionPolling()
      if (!pendingGUrlIds.length) return

      this._schemaInspectionPolling = {
        startedAt: Date.now(),
        gUrlIds: pendingGUrlIds
      }
      this.markPendingSchemaInspectionNodes()
      this.scheduleSchemaInspectionPolling(SCHEMA_INSPECTION_INITIAL_POLLING_INTERVAL)
    },
    restoreSchemaInspectionPolling () {
      const pendingGUrlIds = this.allNodes
        .filter(node => {
          const inspection = node.schemaInspection || {}
          const status = inspection.reportStatus || inspection.status || node.reportStatus || node.status
          return status === 'ing'
        })
        .map(node => Number(node.gUrlId))
        .filter(gUrlId => Number.isFinite(gUrlId))

      if (!pendingGUrlIds.length) return

      this.schemaInspectionRequested = true
      this.startSchemaInspectionPolling([...new Set(pendingGUrlIds)])
    },
    stopSchemaInspectionPolling () {
      clearTimeout(this._schemaInspectionPollingTimer)
      this._schemaInspectionPollingTimer = null
      this._schemaInspectionPolling = null
    },
    scheduleSchemaInspectionPolling (delay) {
      clearTimeout(this._schemaInspectionPollingTimer)
      this._schemaInspectionPollingTimer = setTimeout(() => {
        this.pollSchemaInspectionResult()
      }, delay)
    },
    pollSchemaInspectionResult () {
      const polling = this._schemaInspectionPolling
      if (!polling || !this.dataOption) return

      const elapsed = Date.now() - polling.startedAt
      if (elapsed >= SCHEMA_INSPECTION_MAX_POLLING_DURATION) {
        this.stopSchemaInspectionPolling()
        return
      }

      getSitemapTree(this.dataOption, true)
        .then(result => {
          if (!this._schemaInspectionPolling) return

          this.applySitemapTreeResult(result)
          this.markPendingSchemaInspectionNodes()

          if (this.isSchemaInspectionPollingCompleted()) {
            this.stopSchemaInspectionPolling()
            return
          }

          this.scheduleSchemaInspectionPolling(this.getSchemaInspectionPollingInterval())
        })
        .catch(() => {
          if (!this._schemaInspectionPolling) return

          this.scheduleSchemaInspectionPolling(this.getSchemaInspectionPollingInterval())
        })
    },
    getSchemaInspectionPollingInterval () {
      if (!this._schemaInspectionPolling) return SCHEMA_INSPECTION_SLOW_POLLING_INTERVAL

      const elapsed = Date.now() - this._schemaInspectionPolling.startedAt
      if (elapsed < SCHEMA_INSPECTION_INITIAL_POLLING_DURATION) {
        return SCHEMA_INSPECTION_INITIAL_POLLING_INTERVAL
      }
      if (elapsed < SCHEMA_INSPECTION_FAST_POLLING_DURATION) {
        return SCHEMA_INSPECTION_FAST_POLLING_INTERVAL
      }
      return SCHEMA_INSPECTION_SLOW_POLLING_INTERVAL
    },
    markPendingSchemaInspectionNodes () {
      const polling = this._schemaInspectionPolling
      if (!polling) return

      polling.gUrlIds.forEach(gUrlId => {
        const node = this.nodeByGUrlId[gUrlId]
        if (!node) return
        if (this.isSchemaInspectionNodeCompleted(node)) return

        this.$set(node, 'schemaInspection', {
          ...(node.schemaInspection || {}),
          loading: true,
          reportStatus: 'ing'
        })
      })
    },
    isSchemaInspectionPollingCompleted () {
      const polling = this._schemaInspectionPolling
      if (!polling) return true

      return polling.gUrlIds.every(gUrlId => {
        const node = this.nodeByGUrlId[gUrlId]
        return node && this.isSchemaInspectionNodeCompleted(node)
      })
    },
    isSchemaInspectionNodeCompleted (node) {
      const inspection = node.schemaInspection || {}
      const status = inspection.reportStatus || inspection.status || node.reportStatus || node.status
      const hasResult = [inspection.exists, inspection.score, node.exists, node.score]
        .some(value => value != null)

      if (status && status !== 'ing') return true
      return hasResult && status !== 'ing'
    },
    resetPaneView () {
      this.paneScale = DEFAULT_PANE_SCALE
      this.panePosition = [...DEFAULT_PANE_POSITION]
    },
    toggleThumbnail () {
      this.thumbnailHidden = !this.thumbnailHidden
      this.$nextTick(() => this.setPaneOverflowPosition())
    },
    toggleNodesExpanded () {
      if (this.allNodesExpanded) {
        this.collapseAll()
      } else {
        this.expandAll()
      }
    },
    changeSitemapMode (mode) {
      if (this.journeyOptions.subSid) return
      if (this.sitemapMode === mode || this.apiLoading) return
      this.sitemapMode = mode
      this.loadData()
    },
    onCapture (node, device) {
      const imagePath = parseImagePath(node.imagePath)
      this.capturingDevice = device
      this.capturingPage = {
        ...node,
        url: node.url || `${node.origin || ''}${node.pathname || node.path || '/'}`,
        gUrlId: node.gUrlId || node.urlId || node.key,
        sid: node.sid || this.journeyOptions.sid,
        matchType: node.matchType || this.journeyOptions.matchType,
        imagePath: typeof imagePath === 'object' && imagePath ? imagePath : { desktop: imagePath },
        realCaptureUrl: node.realCaptureUrl || {},
        baApply: node.baApply || { state: null }
      }
    },
    onCaptureProcessing (captureData) {
      const currentImages = normalizeDeviceValues(this.selectedNode && this.selectedNode.imagePath)
      this.captureProcessing = {
        ...captureData,
        nodeKey: this.selectedNode && this.selectedNode._key,
        device: this.capturingDevice,
        previousImagePath: currentImages[this.capturingDevice]
      }
      this.pollCaptureResult()
    },
    pollCaptureResult () {
      clearTimeout(this._capturePollTimer)
      const processing = this.captureProcessing
      if (!processing) return

      checkCaptureExist(processing.sid, processing.urlId, processing.matchType, processing.url)
        .then(result => {
          if (this.captureProcessing !== processing) return
          const images = normalizeDeviceValues(result.imagePath)
          const nextImagePath = images[processing.device]
          if (nextImagePath && nextImagePath !== processing.previousImagePath) {
            const targetNode = this.nodeByKey[processing.nodeKey]
            if (targetNode) {
              const mergedImages = mergeDeviceValues(targetNode.imagePath, result.imagePath)
              this.$set(targetNode, 'imagePath', mergedImages)
              this.$set(targetNode, '_thumbnailUrl', toImageUrl(mergedImages))
              if (result.realCaptureUrl) {
                const mergedUrls = mergeDeviceValues(targetNode.realCaptureUrl, result.realCaptureUrl)
                this.$set(targetNode, 'realCaptureUrl', mergedUrls)
              }
              if (result.captureDate) this.$set(targetNode, 'captureDate', result.captureDate)
            }
            this.captureProcessing = null
            return
          }
          this._capturePollTimer = setTimeout(() => this.pollCaptureResult(), 5000)
        })
        .catch(() => {
          if (this.captureProcessing === processing) {
            this.captureProcessing = null
          }
        })
    },
    toggleNode (node) {
      if (this.expandedMap[node._key]) {
        this.$delete(this.expandedMap, node._key)
        this.collapseDescendants(node)
      } else {
        this.$set(this.expandedMap, node._key, true)
      }
      this.$nextTick(() => this.setPaneOverflowPosition())
    },
    collapseDescendants (node) {
      node.items.forEach(child => {
        this.$delete(this.expandedMap, child._key)
        this.collapseDescendants(child)
      })
    },
    showReport (node) {
      clearTimeout(this._closeNodeReportTimer)
      cancelAnimationFrame(this._openNodeReportFrame)
      this.selectedNodeKey = node._key
      this.reportShowing = true
      this.$nextTick(() => {
        this.adjustPanePositionForReport(node)
        this._openNodeReportFrame = requestAnimationFrame(() => {
          this.openNodeReport = true
        })
      })
    },
    hideReport () {
      cancelAnimationFrame(this._openNodeReportFrame)
      this.openNodeReport = false
      if (this.formerPanePosition) {
        this.panePosition = this.formerPanePosition
        this.formerPanePosition = null
      }
      this._closeNodeReportTimer = setTimeout(() => {
        this.reportShowing = false
        this.selectedNodeKey = null
      }, 500)
    },
    resetReportState () {
      cancelAnimationFrame(this._openNodeReportFrame)
      clearTimeout(this._closeNodeReportTimer)
      this.openNodeReport = false
      this.reportShowing = false
      this.selectedNodeKey = null
      if (this.formerPanePosition) {
        this.panePosition = this.formerPanePosition
        this.formerPanePosition = null
      }
    },
    adjustPanePositionForReport (node) {
      const nodeList = this.$refs.nodeList
      if (!nodeList || !node || !this.paneWrapSize[0] || !this.paneWrapSize[1]) return

      const layout = nodeList.getLayout(node)
      if (!layout) return

      if (!this.formerPanePosition) {
        this.formerPanePosition = this.panePosition.slice()
      }

      const reportWidth = 430
      const nodeCenterX = layout.left + (nodeList.nodeWidth / 2) + this.panePadding[3] + this.paneExtraPadding[3]
      const nodeCenterY = layout.top + (nodeList.getNodeHeight(node) / 2) + this.panePadding[0] + this.paneExtraPadding[0]
      const left = -(nodeCenterX * this.paneScale - (this.paneWrapSize[0] / 2) + (reportWidth / 2))
      const top = -(nodeCenterY * this.paneScale - (this.paneWrapSize[1] / 2))

      this.panePosition = [left, top]
      this.setPaneOverflowPosition()
      if (this._paneBounds && this._paneBounds[2] > left) {
        this.panePosition[0] = Math.max(left, this._paneBounds[2] - reportWidth)
      }
    },
    selectSearchResult (node) {
      const rootKey = node._key.split('-')[0]
      const root = this.roots[Number(rootKey)]
      if (root) this.activeRootKey = root._key
      this.expandAncestors(node)
      this.keyword = ''
      this.showReport(node)
      this.$nextTick(() => this.setPaneOverflowPosition())
    },
    expandAncestors (node) {
      let parentKey = node._parentKey
      while (parentKey) {
        this.$set(this.expandedMap, parentKey, true)
        const parent = this.nodeByKey[parentKey]
        parentKey = parent ? parent._parentKey : null
      }
    },
    expandAll () {
      if (!this.currentRoot) return
      const expandedMap = {}
      const walk = node => {
        if (node.items && node.items.length) {
          expandedMap[node._key] = true
          node.items.forEach(walk)
        }
      }
      walk(this.currentRoot)
      this.expandedMap = expandedMap
      this.$nextTick(() => this.setPaneOverflowPosition())
    },
    collapseAll () {
      this.expandedMap = this.currentRoot ? { [this.currentRoot._key]: true } : {}
      this.$nextTick(() => this.setPaneOverflowPosition())
    },
    setPaneDraggable () {
      const paneWrap = this.$refs.paneWrap
      if (paneWrap) {
        paneWrap.addEventListener('mousedown', this.onPointerDown)
        paneWrap.addEventListener('touchstart', this.onPointerDown)
        paneWrap.addEventListener(wheelEventName, this.onWheel, { passive: false })
      }
    },
    unsetPaneDraggable () {
      const paneWrap = this.$refs.paneWrap
      if (paneWrap) {
        paneWrap.removeEventListener('mousedown', this.onPointerDown)
        paneWrap.removeEventListener('touchstart', this.onPointerDown)
        paneWrap.removeEventListener(wheelEventName, this.onWheel, { passive: false })
      }
    },
    onWheel (e) {
      const deltaX = e.deltaX !== undefined ? e.deltaX : e.wheelDeltaX || 0
      const deltaY = e.deltaY !== undefined ? e.deltaY : e.wheelDeltaY !== undefined ? e.wheelDeltaY : e.detail || e.wheelDelta * -1

      if (e.metaKey || e.ctrlKey) {
        this.zoomPaneAtPoint(e, deltaY)
        e.preventDefault()
        return
      }

      this.setPaneMinPosition()
      if (!this._paneBounds) return
      const left = Math.min(this._paneBounds[0], Math.max(this._paneBounds[2], this.panePosition[0] - deltaX))
      const top = Math.min(this._paneBounds[1], Math.max(this._paneBounds[3], this.panePosition[1] - deltaY))
      cancelAnimationFrame(this._debouncer)
      this._debouncer = requestAnimationFrame(() => {
        this.setPanePositionWithoutTransition(left, top)
      })
      e.preventDefault()
    },
    zoomPaneAtPoint (e, deltaY) {
      const paneWrap = this.$refs.paneWrap
      if (!paneWrap) return

      const rect = paneWrap.getBoundingClientRect()
      const pointX = (e.clientX || rect.left + this.paneWrapSize[0] / 2) - rect.left
      const pointY = (e.clientY || rect.top + this.paneWrapSize[1] / 2) - rect.top
      const scaleFactor = Math.exp(-deltaY * 0.001)
      const scale = Math.max(0.25, Math.min(1.75, this.paneScale * scaleFactor))
      if (scale === this.paneScale) return

      const contentX = (pointX - this.panePosition[0]) / this.paneScale
      const contentY = (pointY - this.panePosition[1]) / this.paneScale
      this.paneScale = scale
      this.panePosition = [
        pointX - contentX * scale,
        pointY - contentY * scale
      ]
      this.setPaneOverflowPosition()
    },
    onPointerDown (e) {
      if (!e.which || e.which === 1) {
        if (e.target.closest('button, input, select')) return
        this._downPoint = getEventPoint(e)
        this._downPosition = this.panePosition
        this.setPaneMinPosition()
        this._dragActivated = false
        document.addEventListener('mousemove', this.onPointerMove)
        document.addEventListener('touchmove', this.onPointerMove, { passive: false })
        document.addEventListener('mouseup', this.onPointerUp)
        document.addEventListener('touchend', this.onPointerUp)
      }
    },
    onPointerMove (e) {
      const point = getEventPoint(e)
      if (!this._dragActivated && (Math.abs(this._downPoint[0] - point[0]) > 2 || Math.abs(this._downPoint[1] - point[1]) > 2)) {
        this._dragActivated = true
        document.body.classList.add('dragging')
      }
      if (this._dragActivated) {
        const left = this._downPosition[0] + (point[0] - this._downPoint[0])
        const top = this._downPosition[1] + (point[1] - this._downPoint[1])
        cancelAnimationFrame(this._debouncer)
        this._debouncer = requestAnimationFrame(() => {
          this.panePosition = [parseInt(left), parseInt(top)]
        })
      }
      e.preventDefault()
    },
    onPointerUp () {
      if (this._dragActivated) {
        cancelAnimationFrame(this._debouncer)
        this.setPaneOverflowPosition()
        document.body.classList.remove('dragging')
      }
      document.removeEventListener('mousemove', this.onPointerMove)
      document.removeEventListener('touchmove', this.onPointerMove)
      document.removeEventListener('mouseup', this.onPointerUp)
      document.removeEventListener('touchend', this.onPointerUp)
    },
    onPaneScaleChange (scale) {
      scale = scale / 100
      if (this.paneScale !== scale) {
        const relatedScale = scale / this.paneScale
        const position = [
          (this.panePosition[0] - this.paneWrapSize[0] * 0.5) * relatedScale + this.paneWrapSize[0] * 0.5,
          (this.panePosition[1] - this.paneWrapSize[1] * 0.5) * relatedScale + this.paneWrapSize[1] * 0.5
        ]
        this.panePosition = position
        this.paneScale = scale
        this.setPaneOverflowPosition()
      }
    },
    onPaneScaleFit () {
      const pane = this.$refs.pane
      if (!pane) return
      const paneOffsetWidth = pane.offsetWidth
      const paneOffsetHeight = pane.offsetHeight
      const paneHorizontalPadding = this.panePadding[1] + this.panePadding[3]
      const paneVerticalPadding = this.panePadding[0] + this.panePadding[2]
      let paneContentWidth = paneOffsetWidth - paneHorizontalPadding - this.paneExtraPadding[1] - this.paneExtraPadding[3]
      let paneContentHeight = paneOffsetHeight - paneVerticalPadding - this.paneExtraPadding[0] - this.paneExtraPadding[2]
      let scale = Math.min(this.paneWrapSize[0] / paneContentWidth, this.paneWrapSize[1] / paneContentHeight)

      paneContentWidth += paneHorizontalPadding * 1 / scale
      paneContentHeight += paneVerticalPadding * 1 / scale
      scale = Math.max(0.25, Math.min(this.paneWrapSize[0] / paneContentWidth, this.paneWrapSize[1] / paneContentHeight))

      const scaledWidth = paneOffsetWidth * scale
      const scaledHeight = paneOffsetHeight * scale
      this.panePosition = [
        -(scaledWidth - this.paneWrapSize[0]) / 2 + (52 / 4 * 1 / scale),
        -(scaledHeight - this.paneWrapSize[1]) / 2
      ]
      this.paneScale = scale
      this.setPaneOverflowPosition()
    },
    movePaneX (direction) {
      const pane = this.$refs.pane
      if (!pane) return

      this.setPaneMinPosition()
      if (!this._paneBounds) return
      const step = Math.max(260, this.paneWrapSize[0] * 0.55)
      const nextX = direction === 'left'
        ? this.panePosition[0] + step
        : this.panePosition[0] - step
      const [minX, , maxX] = this._paneBounds
      const x = Math.min(minX, Math.max(maxX, nextX))
      this.panePosition = [x, this.panePosition[1]]
    },
    getPaneWrapSize () {
      const paneWrap = this.$refs.paneWrap
      if (paneWrap) {
        this.paneWrapSize = [paneWrap.offsetWidth, paneWrap.offsetHeight]
      }
    },
    setPaneMinPosition () {
      const pane = this.$refs.pane
      if (!pane) {
        this._paneBounds = null
        return
      }
      const [paneWrapWidth, paneWrapHeight] = this.paneWrapSize
      const paneWidth = pane.offsetWidth * this.paneScale
      const paneHeight = pane.offsetHeight * this.paneScale
      this._paneBounds = [
        Math.max(0, (paneWrapWidth - paneWidth) / 2),
        Math.max(0, (paneWrapHeight - paneHeight) / 2),
        paneWrapWidth - pane.offsetWidth * this.paneScale,
        paneWrapHeight - pane.offsetHeight * this.paneScale
      ]
      this._paneBounds[2] = Math.min(this._paneBounds[0], this._paneBounds[2])
      this._paneBounds[3] = Math.min(this._paneBounds[1], this._paneBounds[3])
    },
    setPaneOverflowPosition (withoutTransition) {
      this.setPaneMinPosition()
      if (!this._paneBounds) return
      const [paneX, paneY] = this.panePosition
      const [minX, minY, maxX, maxY] = this._paneBounds
      const x = paneX > minX ? minX : maxX > paneX ? maxX : paneX
      const y = paneY > minY ? minY : maxY > paneY ? maxY : paneY
      if (withoutTransition) {
        this.setPanePositionWithoutTransition(x, y)
      } else {
        this.panePosition = [x, y]
      }
    },
    setPanePositionWithoutTransition (x, y) {
      const pane = this.$refs.pane
      if (pane) {
        clearTimeout(this._restoreTransitionTimer)
        pane.classList.add('no-transition')
        this.panePosition = [x, y]
        this._restoreTransitionTimer = setTimeout(() => {
          pane.classList.remove('no-transition')
        }, 200)
      }
    },
    onResize (withOverflowPosition) {
      if (this.$refs.paneWrap) {
        this.getPaneWrapSize()
        withOverflowPosition && this.setPaneOverflowPosition()
        this.setPanePositionWithoutTransition(...this.panePosition)
      }
    }
  }
}
</script>

<style lang="scss">
body.dragging {
  cursor: -webkit-grabbing;
  cursor: grabbing;
}
</style>

<style lang="scss" scoped>
.ba-trend.ba-sitemap {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #e5e5e1;

  .ba-pane-control {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 8;
    width: 52px;
    height: 100%;
  }

  .pane-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: #e5e5e1;
    overflow: hidden;
    cursor: -webkit-grab;
    cursor: grab;
    touch-action: none;

    body.dragging & {
      user-select: none;
      pointer-events: none;
    }

    &.disabled {
      background-color: #dcdcd9;

      .pane {
        background-color: #dcdcd9;
      }

      & /deep/ .sitemap-node:not(.active) {
        opacity: 0.22;
      }

      & /deep/ .more-node {
        opacity: 0.22;
      }
    }

    &.node-reporting {
      .pane {
        transition-timing-function: ease;
      }
    }
  }

  .pane {
    position: relative;
    left: 0;
    top: 0;
    min-width: 100%;
    min-height: 100%;
    box-sizing: border-box;
    background-color: #f4f4f1;
    display: inline-block;
    transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    transform-origin: 0 0;
    will-change: transform;

    &.no-transition,
    body.dragging & {
      transition: none;
    }
  }

  .sitemap-no-data {
    position: absolute;
    left: 52px;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #6f6f6f;
    text-align: center;

    h3 {
      margin: 0 0 10px 0;
      font-size: 20px;
      font-weight: 600;
      color: #3a3a3a;
    }

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .pane-navigate {
    position: absolute;
    top: 50%;
    z-index: 8;
    width: 42px;
    height: 76px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.78);
    box-shadow: 0 12px 18px -14px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(10px);
    transform: translateY(-50%);
    transition: background-color 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease, right 0.5s ease;

    .icon {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &:hover {
      background-color: #ffffff;
      box-shadow: 0 14px 20px -14px rgba(0, 0, 0, 0.45);
      .icon {
        filter: brightness(0.4);
      }
    }

    &.left {
      left: 40px;
    }

    &.right {
      right: 40px;

      &.node-reporting {
        right: 462px;
      }
    }
  }

  .blocker {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    z-index: 9;
  }

  .node-report {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
    width: 444px;
    height: 100%;
    box-shadow: 0 2px 20px -2px rgba(0, 0, 0, 0.08);
    border-left: 1px solid #c9c9c6;
    background-color: #ffffff;
    transition: transform 0.5s ease;

    &.node-report-enter,
    &.node-report-leave-to {
      transform: translate(110%, 0);
    }

    .ba-sitemap-node-report {
      overflow: auto;
      height: 100%;
      position: relative;
      z-index: 3;
    }

    .close-report {
      position: absolute;
      z-index: 2;
      top: 50%;
      left: -20px;
      width: 20px;
      height: 50px;
      padding: 17px 0;
      border: 1px solid #c9c9c6;
      border-right: none;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      transform: translateY(-50%);
      background: #ffffff;

      &::after,
      &::before {
        content: "";
        width: 6px;
        height: 6px;
        border: 1px solid #c9c9c6;
        position: absolute;
        right: 0;
        border-left: none;
      }

      &::after {
        border-top: none;
        border-radius: 0 0 5px 0;
        top: -7px;
      }

      &::before {
        border-bottom: none;
        border-radius: 0 5px 0 0;
        bottom: -7px;
      }
    }
  }
}
</style>
